import { useState, useMemo, useEffect } from 'preact/hooks';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';
import { IcoX, IcoRectangleGroup, IcoArrowLeft } from '@components/icons';
import { Button } from '@components/buttons';
import { Tabs, Tab } from '@components/tabs';
import {
  ThemePanelProps,
  ThemeFont,
  PageTheme,
  ElementType,
  SelectedElementType,
  ActiveTab,
  AppliedFont,
} from './types';
import { ANIMATION_DURATION_MS, BUILT_IN_FONTS, DEFAULT_STYLES } from './constants';
import { getFontImport } from './utils';
import {
  TypographyControls,
  FontSelector,
  handleAddCustomFont,
  TypographyState,
} from './typography-panel';
import { ColorsPanel } from './placeholder-panels';
import { ButtonsPanel } from './buttons-panel';

// Re-export types and utility functions that other modules might need
export type { ThemeFont, AppliedFont, PageTheme, CTATheme } from './types';
export { getFontFamily, getAppliedFontFamily } from './utils';

type ThemePanelState = {
  allFonts: ThemeFont[];
  selectedElementType: SelectedElementType;
  activeTab: ActiveTab;
  isClosing: boolean;
};

export function ThemePanel({ isOpen, onClose, theme, setTheme }: ThemePanelProps) {
  const [state, setState] = useState<ThemePanelState>({
    allFonts: [],
    selectedElementType: 'h1',
    activeTab: 'typography',
    isClosing: false,
  });

  // Initialize fonts when panel opens
  useDidUpdateEffect(() => {
    if (isOpen && state.allFonts.length === 0) {
      const fontList = [
        ...BUILT_IN_FONTS,
        ...(theme.fonts || []).filter((f) => !BUILT_IN_FONTS.some((b) => b.id === f.id)),
      ];
      setState((prev) => ({ ...prev, allFonts: fontList }));
    }
  }, [isOpen]);

  // Add click handlers for element selection when panel is open
  // We listen to document clicks to allow users to click on elements in the page
  // content (h1, h2, h3, or any content) and automatically select that element
  // type in the theme panel for editing its typography settings
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleElementClick(e: MouseEvent) {
      // Find the editable content wrapper - look for .sales-page with data-theme-styles
      const editableWrapper = document.querySelector('.sales-page[data-theme-styles]');
      if (!editableWrapper || !editableWrapper.contains(e.target as Node)) {
        return;
      }

      // Find the closest heading element
      const target = e.target as HTMLElement;
      const closestHeading = target.closest('h1,h2,h3');

      const elementType: ElementType = closestHeading
        ? (closestHeading.tagName.toLowerCase() as ElementType)
        : 'content';

      setState((prev) => ({ ...prev, selectedElementType: elementType }));
    }

    // Add the click listener to the document without capture
    document.addEventListener('click', handleElementClick, false);

    // Cleanup when panel closes or component unmounts
    return () => {
      document.removeEventListener('click', handleElementClick, false);
    };
  }, [isOpen]);

  function handleClose() {
    setState((prev) => ({ ...prev, isClosing: true }));

    setTimeout(() => {
      setState((prev) => ({ ...prev, isClosing: false }));
      onClose();
    }, ANIMATION_DURATION_MS);
  }

  function handleElementTypeChange(e: Event) {
    const value = (e.target as HTMLSelectElement).value as ElementType;
    setState((prev) => ({ ...prev, selectedElementType: value }));
  }

  function handleTabChange(tab: ActiveTab) {
    setState((prev) => ({ ...prev, activeTab: tab }));
  }

  function updateAppliedFont(updates: Partial<AppliedFont>) {
    // Get existing applied font or create a default one
    const existingAppliedFont =
      theme.appliedFonts[state.selectedElementType] || ({} as AppliedFont);

    const newAppliedFonts = {
      ...theme.appliedFonts,
      [state.selectedElementType]: {
        ...existingAppliedFont,
        ...updates,
        // Ensure fontId is always a string
        fontId: updates.fontId || existingAppliedFont.fontId || 'inter',
      },
    };

    // Extract custom fonts (those not in BUILT_IN_FONTS)
    const customFonts = state.allFonts.filter(
      (font) => !BUILT_IN_FONTS.some((builtIn) => builtIn.id === font.id),
    );

    const updatedTheme: PageTheme = {
      fonts: customFonts,
      appliedFonts: newAppliedFonts,
    };

    setTheme(updatedTheme);
  }

  async function onAddCustomFont() {
    await handleAddCustomFont({
      allFonts: state.allFonts,
      setAllFonts: (updater) =>
        setState((prev) => ({
          ...prev,
          allFonts: updater(prev.allFonts),
        })),
      setLocalTheme: (updater) => {
        // Apply the theme change immediately
        const updatedTheme = updater(theme);
        setTheme(updatedTheme);
      },
      selectedElementType: state.selectedElementType,
      setPreviewStyles: (styles) => {
        // Convert the preview styles update to appliedFonts update
        if (state.selectedElementType && styles[state.selectedElementType]?.fontId) {
          updateAppliedFont({ fontId: styles[state.selectedElementType].fontId });
        }
      },
    });
  }

  if (!isOpen && !state.isClosing) {
    return null;
  }

  const fontImports = useMemo(
    () => state.allFonts.map(getFontImport).filter(Boolean).join('\n'),
    [state.allFonts],
  );

  // Get current values with fallbacks
  const selectedElementData = theme.appliedFonts[state.selectedElementType];
  const defaultStyles = DEFAULT_STYLES[state.selectedElementType];

  const currentSize = selectedElementData?.size ?? defaultStyles.size;
  const currentLeading = selectedElementData?.leading ?? defaultStyles.leading;
  const currentMarginBottom = selectedElementData?.marginBottom ?? defaultStyles.marginBottom;

  const typographyState: TypographyState = {
    size: currentSize,
    leading: currentLeading,
    marginBottom: currentMarginBottom,
  };

  function handleTypographyStateChange(updates: Partial<TypographyState>) {
    updateAppliedFont(updates);
  }

  // Transform appliedFonts to preview styles format
  const previewStyles = useMemo(() => {
    const styles: Partial<
      Record<
        ElementType,
        { fontId?: string; fontSize?: number; lineHeight?: number; marginBottom?: number }
      >
    > = {};
    Object.entries(theme.appliedFonts).forEach(([element, applied]) => {
      if (applied) {
        styles[element as ElementType] = {
          fontId: applied.fontId,
          fontSize: applied.size,
          lineHeight: applied.leading,
          marginBottom: applied.marginBottom,
        };
      }
    });
    return styles;
  }, [theme.appliedFonts]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: fontImports }} />
      <div class="fixed left-0 top-0 z-50 w-1/2 h-full">
        {/* Theme panel */}
        <div
          class={`bg-white w-full h-full shadow-2xl flex flex-col ${
            state.isClosing ? 'an-fade-out' : 'an-fade-in-right'
          }`}
        >
          {/* Header */}
          <div class="flex items-center justify-between p-4 border-b bg-white">
            <div class="flex items-center gap-2">
              <Button onClick={handleClose} class="p-2 hover:bg-gray-100 rounded">
                <IcoArrowLeft class="size-5" />
              </Button>
              <IcoRectangleGroup class="size-5" />
              <h2 class="text-lg font-semibold">Page Theme</h2>
            </div>
            <Button onClick={handleClose} class="p-2 hover:bg-gray-100 rounded">
              <IcoX class="size-5" />
            </Button>
          </div>

          {/* Tab Navigation */}
          <Tabs class="border-b-0 mb-0 pl-6 bg-gray-50">
            <Tab
              isSelected={state.activeTab === 'typography'}
              onClick={() => handleTabChange('typography')}
            >
              Typography
            </Tab>
            <Tab
              isSelected={state.activeTab === 'colors'}
              onClick={() => handleTabChange('colors')}
            >
              Colors
            </Tab>
            <Tab
              isSelected={state.activeTab === 'buttons'}
              onClick={() => handleTabChange('buttons')}
            >
              Buttons
            </Tab>
          </Tabs>

          {/* Content Area - Two Column Layout */}
          <div class="flex-1 overflow-hidden">
            {state.activeTab === 'typography' && (
              <div class="flex h-full">
                {/* Left Column - Font Family */}
                <div class="w-1/2 p-6 border-r overflow-y-auto">
                  <FontSelector
                    allFonts={state.allFonts}
                    loading={state.allFonts.length === 0}
                    selectedElementType={state.selectedElementType}
                    previewStyles={previewStyles}
                    localTheme={{
                      fonts: theme.fonts || [],
                      appliedFonts: theme.appliedFonts,
                    }}
                    onFontSelect={(font) => updateAppliedFont({ fontId: font.id })}
                    onAddCustomFont={onAddCustomFont}
                  />
                </div>

                {/* Right Column - Settings */}
                <div class="w-1/2 p-6 overflow-y-auto flex flex-col gap-6">
                  {/* Element Selector - Always Visible */}
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Element Type</label>
                    <select
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={state.selectedElementType || ''}
                      onChange={handleElementTypeChange}
                    >
                      <option value="h1">Main Headlines (H1)</option>
                      <option value="h2">Section Headings (H2)</option>
                      <option value="h3">Subheadings (H3)</option>
                      <option value="content">Body Text</option>
                    </select>
                    <p class="text-xs text-gray-500 mt-1">
                      Click elements in your content or select from this dropdown
                    </p>
                  </div>

                  {/* Settings Controls */}
                  <TypographyControls
                    selectedElementType={state.selectedElementType}
                    state={typographyState}
                    setState={handleTypographyStateChange}
                  />

                  {/* Reset to Default Button */}
                  <div class="flex justify-end">
                    <Button
                      onClick={() => {
                        const defaults = DEFAULT_STYLES[state.selectedElementType];
                        updateAppliedFont({
                          fontId: 'inter',
                          size: defaults.size,
                          leading: defaults.leading,
                          marginBottom: defaults.marginBottom,
                        });
                      }}
                      class="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Reset to default
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {state.activeTab === 'colors' && <ColorsPanel />}
            {state.activeTab === 'buttons' && <ButtonsPanel theme={theme} setTheme={setTheme} />}
          </div>
        </div>
      </div>
    </>
  );
}
